import { useSettings } from 'contexts/Settings/Settings';
import { clamp, getUrl } from 'helpers';
import { Howl } from 'howler';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

const SoundsContext = createContext<SoundsState>({} as SoundsState);

export const useSounds = () => useContext(SoundsContext);

export function withSounds(WrappedComponent: any) {
    return function SoundsWrappedComponent(props: any) {
        return (
            <SoundsContext.Consumer>{state => <WrappedComponent {...props} sounds={state} />}</SoundsContext.Consumer>
        );
    };
}

export function SoundsProvider({ children }: SoundsProviderProps) {
    const [cache, setCache] = useState<{ [index: string]: Howl }>({});
    const [muted, setMuted] = useLocalStorage('sounds.muted', false);
    const settings = useSettings();
    const mutedRef = useRef(muted);
    mutedRef.current = muted;

    useEffect(() => {
        // Update cached sounds mute state
        Object.values(cache).forEach(sound => {
            sound.mute(muted);
        });
    }, [cache, muted]);

    function play(soundName: SoundName, volume = 1) {
        // Get sound from cache
        let sound = cache[soundName];

        if (!sound) {
            // Create sound
            sound = new Howl({
                autoplay: true,
                mute: mutedRef.current,
                src: [getUrl(`/assets/sounds/${soundName}.mp3`)],
            });

            // Store in cache
            setCache(prevCache => ({
                ...prevCache,
                [soundName]: sound,
            }));
        }

        // Play sound
        sound.volume(settings.volume * clamp(volume, 0, 1));
        sound.play();

        return sound;
    }

    const state = {
        muted,
        play,
        setMuted,
    };

    return <SoundsContext.Provider value={state}>{children}</SoundsContext.Provider>;
}
